import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Tooltip, Typography, Avatar, IconButton } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CommentIcon from "@mui/icons-material/Comment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  VideoComment,
  formatVideoTimestamp,
  calculateTimelinePosition,
} from "@/utils/videoUtils";

interface NetworkApiGroup {
  timestamp: number; // in seconds
  leftPercent: number;
  logs: any[];
  hasFailures: boolean;
  hasSuccesses: boolean;
}

interface VideoSeekbarProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  currentTime: number;
  duration: number;
  comments?: VideoComment[];
  networkLogs?: any[];
  recordingStartTime?: number;
  onSeek?: (time: number) => void;
  onCommentClick?: (timestamp: number, commentId: string) => void;
  onFailedApiClick?: (timestamp: number, logIds: string[]) => void;
}

const VideoSeekbar: React.FC<VideoSeekbarProps> = ({
  videoRef,
  currentTime,
  duration,
  comments = [],
  networkLogs = [],
  recordingStartTime = 0,
  onSeek,
  onCommentClick,
  onFailedApiClick,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [hoveredFailedApiId, setHoveredFailedApiId] = useState<string | null>(null);
  const seekbarRef = useRef<HTMLDivElement>(null);

  // Filter comments with videoTimestamp
  const videoComments = comments.filter(
    (c) => c.videoTimestamp !== undefined && c.videoTimestamp !== null
  );

  // Helper to check if a log is failed
  const isFailedLog = (log: any) => log.status >= 400 || log.error === true || log.status === 0;

  // Helper to check if a log is a Fetch/XHR API call
  // Handles both processed type ("Fetch/XHR") and raw type ("fetch", "xhr")
  const isFetchXhrCall = (log: any) => {
    const type = log.type?.toLowerCase();
    return type === "fetch/xhr" || type === "fetch" || type === "xhr" || log.rawType === "fetch" || log.rawType === "xhr";
  };

  // Group only Fetch/XHR API calls by timestamp (within 0.5s threshold)
  const networkApiGroups: NetworkApiGroup[] = useMemo(() => {
    if (!networkLogs || networkLogs.length === 0 || duration <= 0) return [];

    const videoDurationMs = duration * 1000;
    const groups: NetworkApiGroup[] = [];
    const threshold = 0.5; // Group logs within 0.5 seconds

    // Filter to only Fetch/XHR calls
    const fetchXhrLogs = networkLogs.filter(isFetchXhrCall);

    fetchXhrLogs.forEach((log, index) => {
      const logStart = (log.startTime || log.timestamp) - recordingStartTime;
      const timestampSec = logStart / 1000;
      const leftPercent = (logStart / videoDurationMs) * 100;

      if (leftPercent < 0 || leftPercent > 100) return;

      const isFailed = isFailedLog(log);

      // Check if this log fits into an existing group
      const existingGroup = groups.find(
        (g) => Math.abs(g.timestamp - timestampSec) <= threshold
      );

      if (existingGroup) {
        existingGroup.logs.push({ ...log, index, isFailed });
        if (isFailed) existingGroup.hasFailures = true;
        else existingGroup.hasSuccesses = true;
      } else {
        groups.push({
          timestamp: timestampSec,
          leftPercent,
          logs: [{ ...log, index, isFailed }],
          hasFailures: isFailed,
          hasSuccesses: !isFailed,
        });
      }
    });

    return groups;
  }, [networkLogs, recordingStartTime, duration]);

  // Count total APIs and failed APIs
  const totalApis = networkApiGroups.reduce(
    (sum, group) => sum + group.logs.length,
    0
  );
  const totalFailedApis = networkApiGroups.reduce(
    (sum, group) => sum + group.logs.filter((log) => log.isFailed).length,
    0
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [videoRef]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleSeekbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekbarRef.current || duration <= 0) return;

    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
    onSeek?.(newTime);
  };

  const handleSeekbarHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekbarRef.current || duration <= 0) return;

    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    setHoveredTime(percentage * duration);
  };

  const handleCommentMarkerClick = (
    e: React.MouseEvent,
    comment: VideoComment
  ) => {
    e.stopPropagation();
    if (comment.videoTimestamp !== undefined) {
      if (videoRef.current) {
        videoRef.current.currentTime = comment.videoTimestamp;
      }
      onCommentClick?.(comment.videoTimestamp, comment._id);
    }
  };

  const handleNetworkApiMarkerClick = (
    e: React.MouseEvent,
    group: NetworkApiGroup
  ) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = group.timestamp;
    }
    const logIds = group.logs.map((log) => log.id || `net-${log.index}`);
    onFailedApiClick?.(group.timestamp, logIds);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {/* Seekbar with Timeline Events Container */}
      <Box
        ref={seekbarRef}
        onClick={handleSeekbarClick}
        onMouseMove={handleSeekbarHover}
        onMouseLeave={() => setHoveredTime(null)}
        sx={{
          position: "relative",
          height: 20,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Seekbar Track */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: "rgba(255,255,255,0.3)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Progress */}
          <Box
            sx={{
              width: `${progressPercent}%`,
              height: "100%",
              backgroundColor: "#fff",
              borderRadius: 2,
              transition: "width 0.1s linear",
            }}
          />
        </Box>

        {/* Hover Time Indicator */}
        {hoveredTime !== null && (
          <Box
            sx={{
              position: "absolute",
              left: `${(hoveredTime / duration) * 100}%`,
              transform: "translateX(-50%)",
              bottom: 20,
              backgroundColor: "rgba(0,0,0,0.8)",
              color: "#fff",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.75rem",
              pointerEvents: "none",
            }}
          >
            {formatVideoTimestamp(hoveredTime)}
          </Box>
        )}

        {/* Timeline Events Container - Comment & Failed API Markers */}
        <Box
          className="timeline-events-container"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            pointerEvents: "none",
          }}
        >
          {/* Network API Markers - Blue for success, Red for failed */}
          {networkApiGroups.map((group, index) => {
            const uniqueId = `network-api-${index}`;
            const isHovered = hoveredFailedApiId === uniqueId;
            const isNearCurrent = Math.abs(group.timestamp - currentTime) < 1;

            // Determine color based on status: red if any failures, blue if all success
            const hasOnlyFailures = group.hasFailures && !group.hasSuccesses;
            const hasOnlySuccesses = group.hasSuccesses && !group.hasFailures;
            const hasMixed = group.hasFailures && group.hasSuccesses;

            const failedCount = group.logs.filter((log) => log.isFailed).length;
            const successCount = group.logs.length - failedCount;

            // Color scheme
            const bgColor = hasOnlySuccesses
              ? (isNearCurrent ? "#1565c0" : "#2196f3") // Blue for success
              : hasOnlyFailures
                ? (isNearCurrent ? "#d32f2f" : "#f44336") // Red for failed
                : (isNearCurrent ? "#ed6c02" : "#ff9800"); // Orange for mixed

            const glowColor = hasOnlySuccesses
              ? "rgba(33, 150, 243, 0.8)"
              : hasOnlyFailures
                ? "rgba(244, 67, 54, 0.8)"
                : "rgba(255, 152, 0, 0.8)";

            return (
              <Tooltip
                key={uniqueId}
                title={
                  <Box sx={{ p: 0.5, maxWidth: 300 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                        color: hasOnlySuccesses ? "#90caf9" : hasOnlyFailures ? "#ff6b6b" : "#ffb74d",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {group.logs.length} API Call{group.logs.length > 1 ? "s" : ""}
                      {hasMixed && ` (${successCount} ✓, ${failedCount} ✗)`}
                    </Typography>
                    {/* Scrollable container for all API logs */}
                    <Box
                      sx={{
                        maxHeight: 200,
                        overflowY: "auto",
                        overflowX: "hidden",
                        pr: 0.5,
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "rgba(255,255,255,0.1)",
                          borderRadius: "2px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "rgba(255,255,255,0.3)",
                          borderRadius: "2px",
                          "&:hover": {
                            background: "rgba(255,255,255,0.5)",
                          },
                        },
                      }}
                    >
                      {group.logs.map((log, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{
                            display: "block",
                            color: log.isFailed ? "#ff8a80" : "#b9f6ca",
                            fontSize: "0.7rem",
                            wordWrap: "break-word",
                            py: 0.25,
                            borderBottom: i < group.logs.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                          }}
                        >
                          {log.isFailed ? "✗" : "✓"} {log.method} {log.status || "Error"} - {log.name?.substring(0, 35)}
                          {log.name?.length > 35 ? "..." : ""}
                        </Typography>
                      ))}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 500,
                        mt: 0.5,
                        pt: 0.5,
                        borderTop: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      at {formatVideoTimestamp(group.timestamp)}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  className="timeline-event network-api-marker"
                  onMouseEnter={() => setHoveredFailedApiId(uniqueId)}
                  onMouseLeave={() => setHoveredFailedApiId(null)}
                  onClick={(e) => handleNetworkApiMarkerClick(e, group)}
                  sx={{
                    position: "absolute",
                    left: `${group.leftPercent}%`,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    minWidth: isHovered ? 32 : 26,
                    height: isHovered ? 18 : 14,
                    borderRadius: "8px",
                    backgroundColor: bgColor,
                    cursor: "pointer",
                    pointerEvents: "auto",
                    zIndex: isHovered ? 12 : 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                    px: 0.5,
                    transition: "all 0.15s ease",
                    border: "2px solid #fff",
                    boxShadow: isHovered
                      ? `0 0 12px ${glowColor}`
                      : "0 2px 4px rgba(0,0,0,0.4)",
                    "&:hover": {
                      transform: "translate(-50%, -50%) scale(1.1)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isHovered ? "0.55rem" : "0.5rem",
                      fontWeight: "bold",
                      color: "#fff",
                      lineHeight: 1,
                    }}
                  >
                    XHR
                  </Typography>
                  {group.logs.length > 1 && (
                    <Typography
                      sx={{
                        fontSize: isHovered ? "0.55rem" : "0.45rem",
                        fontWeight: "bold",
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        borderRadius: "4px",
                        px: 0.25,
                        lineHeight: 1,
                      }}
                    >
                      {group.logs.length}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}

          {/* Comment Markers */}
          {videoComments.map((comment, index) => {
            const leftPercent = calculateTimelinePosition(
              comment.videoTimestamp || 0,
              duration
            );

            if (leftPercent < 0 || leftPercent > 100) return null;

            const uniqueId = comment._id || `comment-${index}`;
            const isHovered = hoveredCommentId === uniqueId;
            const isNearCurrent =
              Math.abs((comment.videoTimestamp || 0) - currentTime) < 1;

            return (
              <Tooltip
                key={uniqueId}
                title={
                  <Box sx={{ p: 0.5, maxWidth: 250 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          fontSize: "0.7rem",
                          bgcolor: "#7c4dff",
                        }}
                      >
                        {comment.createdBy?.fullName?.charAt(0)?.toUpperCase() ||
                          "U"}
                      </Avatar>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: "bold", color: "#fff" }}
                      >
                        {comment.createdBy?.fullName || "Unknown"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,255,255,0.9)",
                        wordWrap: "break-word",
                        mb: 0.5,
                      }}
                    >
                      {comment.comment.length > 100
                        ? `${comment.comment.substring(0, 100)}...`
                        : comment.comment}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 500,
                      }}
                    >
                      at {formatVideoTimestamp(comment.videoTimestamp || 0)}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  className="timeline-event comment-marker"
                  onMouseEnter={() => setHoveredCommentId(uniqueId)}
                  onMouseLeave={() => setHoveredCommentId(null)}
                  onClick={(e) => handleCommentMarkerClick(e, comment)}
                  sx={{
                    position: "absolute",
                    left: `${leftPercent}%`,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: isHovered ? 18 : 12,
                    height: isHovered ? 18 : 12,
                    borderRadius: "50%",
                    backgroundColor: isNearCurrent ? "#e91e63" : "#7c4dff",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    zIndex: isHovered ? 10 : 5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.15s ease",
                    border: "2px solid #fff",
                    boxShadow: isHovered
                      ? "0 0 12px rgba(124, 77, 255, 0.8)"
                      : "0 2px 4px rgba(0,0,0,0.4)",
                    "&:hover": {
                      transform: "translate(-50%, -50%) scale(1.2)",
                    },
                  }}
                >
                  <CommentIcon
                    sx={{
                      fontSize: isHovered ? 10 : 8,
                      color: "#fff",
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Playhead */}
        <Box
          sx={{
            position: "absolute",
            left: `${progressPercent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: "#fff",
            boxShadow: "0 0 4px rgba(0,0,0,0.5)",
            zIndex: 15,
            transition: "left 0.1s linear",
          }}
        />
      </Box>

      {/* Controls Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Play/Pause */}
          <IconButton
            size="small"
            onClick={handlePlayPause}
            sx={{ color: "#fff" }}
          >
            {isPlaying ? (
              <PauseIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
          </IconButton>

          {/* Volume */}
          <IconButton
            size="small"
            onClick={handleMuteToggle}
            sx={{ color: "#fff" }}
          >
            {isMuted ? (
              <VolumeOffIcon fontSize="small" />
            ) : (
              <VolumeUpIcon fontSize="small" />
            )}
          </IconButton>

          {/* Time Display */}
          <Typography variant="caption" sx={{ color: "#fff", ml: 1 }}>
            {formatVideoTimestamp(currentTime)} / {formatVideoTimestamp(duration)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* API Count Badge - Shows total and failed count */}
          {totalApis > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#fff",
                backgroundColor: totalFailedApis > 0
                  ? "rgba(244, 67, 54, 0.9)"
                  : "rgba(33, 150, 243, 0.9)",
                px: 1,
                py: 0.25,
                borderRadius: 10,
                fontSize: "0.7rem",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 12 }} />
              {totalApis} XHR{totalFailedApis > 0 && ` (${totalFailedApis} failed)`}
            </Box>
          )}

          {/* Comment Count Badge */}
          {videoComments.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#fff",
                backgroundColor: "rgba(124, 77, 255, 0.8)",
                px: 1,
                py: 0.25,
                borderRadius: 10,
                fontSize: "0.7rem",
              }}
            >
              <CommentIcon sx={{ fontSize: 12 }} />
              {videoComments.length}
            </Box>
          )}

          {/* Fullscreen */}
          <IconButton
            size="small"
            onClick={handleFullscreen}
            sx={{ color: "#fff" }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoSeekbar;
